import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, forkJoin, from, map, merge, of, switchMap } from 'rxjs';

import { ContactsRestService } from '../../../shared/services/contacts/contacts.rest.service';
import { DeviceService } from '../../../shared/services/device.service';
import { SupportLinksService } from '../../../shared/services/support-links.service';
import { StorageInformationService } from '../device-settings/storage-management/components/storage-information/storage-information.service';
import { MenuConsentService } from '../services/menu-consent.service';

import { MenuActions } from './menu.action';
import { SubscriptionService } from '@hunting-nature/Secacam/shared/services/subscription.service';
import { ShopService } from '@hunting-nature/Secacam/shared/services/shop.service';

@Injectable()
export class MenuEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly storageInformationService = inject(StorageInformationService);
  private readonly deviceService = inject(DeviceService);
  private readonly contactsRestService = inject(ContactsRestService);
  private readonly supportLinksService = inject(SupportLinksService);
  private readonly menuConsentService = inject(MenuConsentService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly shopService = inject(ShopService);

  menuEnteredData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuActions.menuEntered),
      switchMap(({ contactsAlreadyLoaded }) =>
        merge(
          this.storageInformationService.getFreeDeviceStorageSize$().pipe(
            map((freeStorageSize: number | undefined) => MenuActions.menuEnteredStorageSucceeded({ freeSpace: freeStorageSize })),
            catchError((error: unknown) => {
              console.error('[MenuEffects] Fetch storage size failed:', error);
              return of(MenuActions.menuEnteredStorageFailed({ error }));
            })
          ),
          contactsAlreadyLoaded
            ? EMPTY
            : this.contactsRestService.fetchAllContacts$().pipe(
                map(contacts => MenuActions.menuEnteredContactsSucceeded({ contacts })),
                catchError((error: unknown) => {
                  console.error('[MenuEffects] Load contacts failed:', error);
                  return of(MenuActions.menuEnteredContactsFailed({ error }));
                })
              )
        )
      )
    )
  );

  openAppSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openAppSettings),
        switchMap(() =>
          from(this.deviceService.openAppSettings$()).pipe(
            map(() => MenuActions.openAppSettingsSucceeded()),
            catchError((error: unknown) => {
              console.error('Failed opening app settings:', error);
              return of(MenuActions.openAppSettingsFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  openFaq$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openFaq),
        switchMap(() => from(this.supportLinksService.openFaq$()))
      ),
    { dispatch: false }
  );

  openContactSupport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openContactSupport),
        switchMap(() => from(this.supportLinksService.openContactSupport$()))
      ),
    { dispatch: false }
  );

  upgradePlan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.upgradePlan),
        switchMap(() => from(this.shopService.openSubscriptionManager()))
      ),
    { dispatch: false }
  );

  openAddCreditsSite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openAddCreditsSite),
        switchMap(() => from(this.supportLinksService.openAddCreditsSite$()))
      ),
    { dispatch: false }
  );

  openBilling$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openBilling),
        switchMap(() => from(this.supportLinksService.openBilling$()))
      ),
    { dispatch: false }
  );

  openInvoices$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuActions.openInvoices),
        switchMap(() => from(this.supportLinksService.openInvoices$()))
      ),
    { dispatch: false }
  );

  menuEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuActions.menuEntered),
      switchMap(({ language }) =>
        this.menuConsentService.loadConsentsWithScheduledRefresh$(language).pipe(
          map(consents => MenuActions.menuEnteredSucceeded({ consents })),
          catchError((error: unknown) => {
            console.error('[MenuEffects] Load consents on menu enter failed:', error);
            return of(MenuActions.menuEnteredFailed({ error }));
          })
        )
      )
    )
  );

  subscriptionCardEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuActions.subscriptionCardEntered),
      switchMap(() =>
        forkJoin({
          subscriptionDetails: this.subscriptionService.getSubscriptionDetails$(),
          creditDetails: this.subscriptionService.getPrepaidCreditsDetails$(),
        }).pipe(
          map(({ subscriptionDetails, creditDetails }) => MenuActions.subscriptionCardEnterSucceeded({ subscriptionDetails, creditDetails })),
          catchError((error: unknown) => of(MenuActions.subscriptionCardEnterFailed({ error })))
        )
      )
    )
  );

  subscriptionsAndPlansPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuActions.subscriptionsAndPlansPageEntered),
      switchMap(() =>
        forkJoin({
          subscriptionDetails: this.subscriptionService.getSubscriptionDetails$(),
          creditDetails: this.subscriptionService.getPrepaidCreditsDetails$(),
        }).pipe(
          map(({ subscriptionDetails, creditDetails }) => MenuActions.subscriptionsAndPlansPageEnteredSucceeded({ subscriptionDetails, creditDetails })),
          catchError((error: unknown) => of(MenuActions.subscriptionsAndPlansPageEnteredFailed({ error })))
        )
      )
    )
  );
}
